import type {
  PlaybookId,
  PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import {
  asPlaybookNodeSampleToolResultId,
  type PlaybookNodeSampleToolResultCriterionPayload,
  type PlaybookNodeSampleToolResultId,
  type PlaybookNodeSampleToolResultOutcomePayload,
  type PlaybookNodeSampleToolResultPayload,
  type PlaybookNodeSampleToolResultSectionPayload,
} from "@repo/app-types/entities/playbookNodeSampleToolResult";
import type { UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useDeleteSampleToolResultForCriterionMutation,
  useLazyFetchSampleToolResultsQuery,
} from "../api/entities/playbook.js";

const SAMPLE_TOOL_NAMES = new Set([
  "getSampleCriteriaScoreDistributionAndOutcomeCorrelationTool",
  "getSampleSectionApplicabilityTool",
  "getSampleOutcomeDistributionTool",
]);

/**
 * One sample-tool result entry, keyed by the saved row id (which is the
 * `sample_tool_result_id` returned by the agent tool's output schema).
 * Discriminated on `kind` (via the payload): criterion entries carry
 * outcome-correlation aggregates, section entries carry applicability
 * aggregates.
 */
export type SampleToolResultEntry = {
  id: PlaybookNodeSampleToolResultId;
  playbook_node_id: PlaybookNodeId;
} & PlaybookNodeSampleToolResultPayload;

export type SampleToolResultCriterionEntry = {
  id: PlaybookNodeSampleToolResultId;
  playbook_node_id: PlaybookNodeId;
} & PlaybookNodeSampleToolResultCriterionPayload;

export type SampleToolResultSectionEntry = {
  id: PlaybookNodeSampleToolResultId;
  playbook_node_id: PlaybookNodeId;
} & PlaybookNodeSampleToolResultSectionPayload;

export type SampleToolResultOutcomeEntry = {
  id: PlaybookNodeSampleToolResultId;
  playbook_node_id: PlaybookNodeId;
} & PlaybookNodeSampleToolResultOutcomePayload;

export function isSectionSampleToolResultEntry(
  entry: SampleToolResultEntry,
): entry is SampleToolResultSectionEntry {
  return entry.kind === "existing_section" || entry.kind === "new_section";
}

export function isOutcomeSampleToolResultEntry(
  entry: SampleToolResultEntry,
): entry is SampleToolResultOutcomeEntry {
  return entry.kind === "outcome";
}

export function isCriterionSampleToolResultEntry(
  entry: SampleToolResultEntry,
): entry is SampleToolResultCriterionEntry {
  return entry.kind === "existing_criterion" || entry.kind === "new_criterion";
}

export interface UseSampleToolResultsResult {
  /**
   * Map of `sample_tool_result_id → entry`. Populated as the assistant
   * runs sample tools during its turns.
   */
  results: Readonly<
    Record<PlaybookNodeSampleToolResultId, SampleToolResultEntry>
  >;

  /**
   * Fetch a batch of `sample_tool_result_id`s and merge them into `results`,
   * overwriting any existing entry for the same id. The fetch always hits the
   * server (no cached value) because a refined new-criterion proposal comes
   * back under the same id with updated content, so a cached payload would be
   * stale.
   */
  addIdsToFetch: (ids: readonly PlaybookNodeSampleToolResultId[]) => void;

  /**
   * Drop the entry for `playbookNodeId` from the local results map. Used
   * when the user accepts or discards a suggestion — both make the saved
   * tool result no longer applicable to the current criterion state.
   */
  removeResultForNode: (playbookNodeId: PlaybookNodeId) => void;

  /**
   * Drop every entry. Use after a lifecycle mutation (publish, draft-discard,
   * rollback) — the server deletes the rows in
   * `playbook_node_sample_tool_result` on those transitions, so the
   * client cache would otherwise display stale ghosts and suggestions.
   */
  clearResults: () => void;
}

/**
 * High-level playbook-editor variable storing sample-tool results the
 * AI assistant has produced this session. Populated only by detecting
 * agent turn completion in `ConfigurationAssistantChat` and pushing the
 * captured `sample_tool_result_id`s here via `addIdsToFetch` — the map
 * starts empty on every mount; saved DB rows are not rehydrated.
 *
 * Storage shape: `Record<PlaybookNodeSampleToolResultId, entry>`, where
 * `entry` is `{ id, playbook_node_id } & PlaybookNodeSampleToolResultPayload`.
 */
export function useSampleToolResults(
  playbookId: PlaybookId | null,
): UseSampleToolResultsResult {
  const [results, setResults] = useState<
    Record<PlaybookNodeSampleToolResultId, SampleToolResultEntry>
  >({});

  // Reset state when the playbook changes — the results are scoped to
  // the playbook being edited.
  const prevPlaybookIdRef = useRef(playbookId);
  if (prevPlaybookIdRef.current !== playbookId) {
    prevPlaybookIdRef.current = playbookId;
    setResults({});
  }

  const [triggerFetch] = useLazyFetchSampleToolResultsQuery();

  const addIdsToFetch = useCallback(
    (ids: readonly PlaybookNodeSampleToolResultId[]) => {
      if (!playbookId || ids.length === 0) {
        return;
      }
      // The same id can appear twice within one turn; collapse before fetching.
      const uniqueIds = [...new Set(ids)];
      void (async () => {
        try {
          // `preferCacheValue: false` forces a fresh request even for ids
          // already loaded this session — a refined new-criterion proposal is
          // re-saved under the same id, so a cached payload would be stale.
          const fetched = await triggerFetch(
            { playbookId, ids: uniqueIds },
            false,
          ).unwrap();
          if (fetched.length === 0) {
            return;
          }
          setResults((prev) => {
            const next = { ...prev };
            for (const entry of fetched) {
              next[entry.id] = entry;
            }
            return next;
          });
        } catch {
          // Network / permission failure — leave existing results untouched.
        }
      })();
    },
    [playbookId, triggerFetch],
  );

  const [deleteForCriterion] = useDeleteSampleToolResultForCriterionMutation();

  const removeResultForNode = useCallback(
    (playbookNodeId: PlaybookNodeId) => {
      setResults((prev) => {
        let didChange = false;
        const next: Record<
          PlaybookNodeSampleToolResultId,
          SampleToolResultEntry
        > = {};
        for (const [id, entry] of Object.entries(prev) as [
          PlaybookNodeSampleToolResultId,
          SampleToolResultEntry,
        ][]) {
          if (entry.playbook_node_id === playbookNodeId) {
            didChange = true;
            continue;
          }
          next[id] = entry;
        }
        return didChange ? next : prev;
      });
      // Drop the saved row server-side so the DB stays in sync with the
      // in-memory state — otherwise rows would accumulate on the server
      // until the next publish / discard-draft / rollback wipes the
      // playbook's full set. Fire-and-forget; the local state already
      // updated. New-playbook (`playbookId === null`) can't have saved rows.
      if (playbookId) {
        void deleteForCriterion({ playbookId, playbookNodeId });
      }
    },
    [playbookId, deleteForCriterion],
  );

  const clearResults = useCallback(() => {
    setResults({});
  }, []);

  return useMemo(
    () => ({ results, addIdsToFetch, removeResultForNode, clearResults }),
    [results, addIdsToFetch, removeResultForNode, clearResults],
  );
}

/**
 * Whenever a turn finishes (`isRunning` transitions from `true` to `false`),
 * scan the chat's last message — if it's an assistant message — for completed
 * sample-tool invocations, and push their `sample_tool_result_id`s into
 * `addIdsToFetch`. If the tail message isn't from the assistant (cancellation
 * paths, etc), the turn is ignored.
 */
export function useCollectSampleToolResultIdsOnTurnComplete(options: {
  messages: readonly UIMessage[];
  isRunning: boolean;
  addIdsToFetch: (ids: readonly PlaybookNodeSampleToolResultId[]) => void;
}): void {
  const { messages, isRunning, addIdsToFetch } = options;
  const prevIsRunningRef = useRef(false);

  useEffect(() => {
    const justFinished = prevIsRunningRef.current && !isRunning;
    prevIsRunningRef.current = isRunning;
    if (!justFinished) {
      return;
    }

    const lastMessage = messages.at(-1);
    if (lastMessage?.role !== "assistant") {
      return;
    }

    const ids: PlaybookNodeSampleToolResultId[] = [];
    for (const part of lastMessage.parts) {
      // AI SDK tool parts are `dynamic-tool` or `tool-${name}`.
      const toolName =
        part.type === "dynamic-tool"
          ? part.toolName
          : part.type.startsWith("tool-")
            ? part.type.slice("tool-".length)
            : null;
      if (!toolName || !SAMPLE_TOOL_NAMES.has(toolName)) {
        continue;
      }
      if (!("state" in part) || part.state !== "output-available") {
        continue;
      }
      const output = (part as { output?: unknown }).output;
      if (!output || typeof output !== "object") {
        continue;
      }
      const rawId = (output as { sample_tool_result_id?: unknown })
        .sample_tool_result_id;
      if (typeof rawId !== "string") {
        continue;
      }
      try {
        ids.push(asPlaybookNodeSampleToolResultId(rawId));
      } catch {
        // Tool returned a non-uuid `sample_tool_result_id`; skip.
      }
    }
    if (ids.length > 0) {
      addIdsToFetch(ids);
    }
  }, [messages, isRunning, addIdsToFetch]);
}
